// src/content/insights/the-problem-is-not-subjectivity.jsx

import ExternalLink from "../../../components/insights/ExternalLink";
import Figure from "../../../components/insights/Figure";
import FigureRef from "../../../components/insights/FigureRef";
import Quote from "../../../components/insights/Quote";

export const article = {
  slug: "the-problem-is-not-subjectivity",

  meta: {
    title:
      "The Problem Is Not Subjectivity — It Is the Lack of Infrastructure",

    publishedAt: "2026-04-13",

    category: "Voice science",

    author: [
      {
        name: "Neus Calaf",
        role: "Founder, All-Voiced",
      },
    ],

    excerpt:
      "Reflections after presenting at PEVoC on why auditory-perceptual voice evaluation may need infrastructure rather than attempts to eliminate subjectivity.",

    coverImage:
      "/images/insights/2026/the-problem-is-not-subjectivity/pevoc-presentation.png",
  },

  tags: [
    "PEVoC",
    "auditory-perceptual-evaluation",
    "voice-science",
    "infrastructure",
    "listening",
    "research-methodology",
  ],

  related: [],

  wordCount: 938,

  Component: function Article() {
    return (
      <>
        <p>
          In March 2026, during the{" "} <ExternalLink href="https://www.pevoc2026.com/">
            16th Pan-European Voice Conference (PEVoC 16) and Voice Istanbul 2026
          </ExternalLink>
          , I presented a talk titled
          <em>
            {" "}
            Building Infrastructure for Auditory-Perceptual Voice Evaluation in
            Research and Education.
          </em>
        </p>

        <p>
          The presentation emerged from a growing methodological concern surrounding
          auditory-perceptual voice evaluation:
          we rely heavily on listening across clinical practice, education, and
          research, yet the systems supporting that listening often remain fragmented,
          difficult to compare, and hard to investigate systematically.
        </p>

        <p>
          While preparing the talk, I increasingly found myself returning to the same
          idea:
        </p>

        <Quote>
          The problem is not subjectivity — it is the lack of infrastructure.
        </Quote>

        <p>
          That sentence became the conceptual center of the presentation.
        </p>

        <p>
          For decades, auditory-perceptual voice evaluation has often been
          described primarily through its limitations:
          subjective,
          difficult to standardize,
          hard to reproduce,
          dependent on expertise,
          and difficult to compare across listeners or contexts.
        </p>

        <p>
          But during the preparation of this talk, I increasingly felt that
          subjectivity itself might not be the real methodological problem.
        </p>

        <p>
          Perhaps the deeper issue was that we had never built enough systems to
          work with perceptual subjectivity properly.
        </p>

        <h2>
          Listening without infrastructure
        </h2>

        <p>
          Auditory-perceptual evaluation is central to voice science.
        </p>

        <p>
          We rely on listening:
        </p>

        <ul>
          <li>in clinical voice assessment,</li>
          <li>in professional training,</li>
          <li>in research,</li>
          <li>and in how we interpret vocal function and disorder.</li>
        </ul>

        <p>
          Yet many perceptual workflows still depend heavily on fragmented local
          practices:
          isolated training,
          inconsistent feedback,
          limited calibration opportunities,
          and little structured aggregation of perceptual judgments.
        </p>

        <p>
          This problem was summarized during the presentation as shown in{" "}
          <FigureRef id="fig1" />.
        </p>

        <Figure
          id="fig1"
          variant="slide"
          src="/images/insights/2026/the-problem-is-not-subjectivity/lack-of-infrastructure-slide.png"
          caption="Slide presented at PEVoC introducing infrastructure as the missing methodological layer in auditory-perceptual voice evaluation."
        />

        <p>
          Importantly, the presentation did not argue against subjectivity.
        </p>

        <p>
          In fact, one of the central ideas was almost the opposite:
          subjectivity does not necessarily mean randomness.
          It means perspective.
        </p>

        <p>
          The challenge is that we often lack the systems required to make those
          perspectives traceable, comparable, calibratable, and reusable.
        </p>

        <h2>
          Infrastructure as a methodological layer
        </h2>

        <p>
          The word infrastructure can sound abstract.
        </p>

        <p>
          But in the context of auditory-perceptual evaluation, the idea was
          actually very concrete:
        </p>

        <ul>
          <li>standardized digital tasks,</li>
          <li>structured response capture,</li>
          <li>traceable user performance,</li>
          <li>feedback and calibration systems,</li>
          <li>aggregation of perceptual ratings,</li>
          <li>and reusable datasets for research and education.</li>
        </ul>

        <p>
          This infrastructural framing was introduced explicitly during the
          presentation, as illustrated in <FigureRef id="fig2" />.
        </p>

        <Figure
          id="fig2"
          variant="slide"
          src="/images/insights/2026/the-problem-is-not-subjectivity/missing-layer-slide.png"
          caption="Conceptual framing presented at PEVoC: infrastructure as a missing methodological layer for perceptual voice evaluation."
        />

        <p>
          In many scientific domains, infrastructure is what transforms isolated
          actions into cumulative systems of knowledge.
        </p>

        <p>
          Medical imaging, genomics, and other data-intensive fields rely on
          standardized protocols, shared datasets, traceable workflows, and
          interoperable systems.
        </p>

        <p>
          Auditory-perceptual voice evaluation, however, has often remained
          comparatively difficult to structure at scale.
        </p>

        <p>
          The presentation therefore explored a simple but important idea:
          perhaps listening itself deserved infrastructure.
        </p>

        <Quote>
          Perceptual judgments are not only impressions — they can also become
          data.
        </Quote>

        <h2>
          From judgments to data
        </h2>

        <p>
          One of the key methodological shifts explored during the presentation
          involved reframing perceptual judgments as analyzable structured data.
        </p>

        <p>
          Traditionally, auditory-perceptual evaluation is often discussed in
          terms of:
        </p>

        <ul>
          <li>individual expert opinions,</li>
          <li>limited comparability,</li>
          <li>or one-off training exercises.</li>
        </ul>

        <p>
          But once perceptual responses are collected systematically, aggregated
          across listeners, and contextualized within structured workflows, new
          scientific questions begin to emerge.
        </p>

        <p>
          This transition was summarized in <FigureRef id="fig3" />.
        </p>

        <Figure
          id="fig3"
          variant="slide"
          src="/images/insights/2026/the-problem-is-not-subjectivity/from-judgments-to-data-slide.png"
          caption="Methodological shift proposed during the presentation: from isolated judgments toward structured perceptual data."
        />

        <p>
          Instead of treating variability exclusively as a limitation,
          perceptual variability itself can become something observable and
          analyzable.
        </p>

        <p>
          Consensus can be modeled.
          Calibration can be monitored.
          Agreement can be contextualized.
          Stimulus difficulty can potentially be explored empirically.
        </p>

        <p>
          And perhaps most importantly, perceptual evaluation can begin to
          accumulate over time rather than disappearing after each isolated
          interaction.
        </p>

        <h2>
          Variability as information
        </h2>

        <p>
          Another important idea explored during the talk was that disagreement
          itself may contain information.
        </p>

        <p>
          In many contexts, perceptual variability is treated primarily as
          undesirable noise.
        </p>

        <p>
          But once perceptual data become structured and large-scale, variability
          can also become scientifically informative.
        </p>

        <p>
          Structured perceptual systems may allow us to study:
        </p>

        <ul>
          <li>differences between novice and expert listeners,</li>
          <li>cross-cultural perceptual variation,</li>
          <li>stability across tasks and contexts,</li>
          <li>stimulus difficulty,</li>
          <li>calibration needs,</li>
          <li>and the emergence of perceptual consensus.</li>
        </ul>

        <p>
          This perspective was summarized during the presentation as shown in{" "}
          <FigureRef id="fig4" />.
        </p>

        <Figure
          id="fig4"
          variant="slide"
          src="/images/insights/2026/the-problem-is-not-subjectivity/variability-slide.png"
          caption="Variability reframed as a potential source of information rather than only as noise."
        />

        <Quote>
          From disagreement → insight.
        </Quote>

        <p>
          This represented an important conceptual shift for me personally.
        </p>

        <p>
          Instead of asking only whether listeners agree,
          the presentation began exploring a different question:
          how does agreement emerge, and under which conditions?
        </p>

        <h2>
          Listening becomes cumulative
        </h2>

        <p>
          Toward the end of the presentation, the discussion moved beyond
          isolated educational workflows toward broader research possibilities.
        </p>

        <p>
          If listening becomes structured, traceable, and reusable,
          auditory-perceptual evaluation may begin to support:
        </p>

        <ul>
          <li>scalable calibration systems,</li>
          <li>shared perceptual reference datasets,</li>
          <li>cross-cultural research collaborations,</li>
          <li>global perceptual datasets,</li>
          <li>and reproducible educational workflows.</li>
        </ul>

        <p>
          The presentation did not attempt to propose a finalized methodological
          framework.
        </p>

        <p>
          Instead, it explored the possibility that auditory-perceptual voice
          evaluation may require a new infrastructural layer capable of supporting
          listening as something traceable, comparable, and cumulative.
        </p>

        <p>
          Increasingly, All-Voiced was starting to feel less like an isolated training
          platform and more like a broader infrastructure for perceptual voice
          evaluation.
        </p>

        <Quote>
          Listening is not vague.  
          It is not secondary.  
          It is foundational.
        </Quote>
      </>
    );
  },
};